import api from './client'

export async function getUsers() {
  const res = await api.get('/enterprise/users')
  return res.data
}

/**
 * Activate / deactivate a member of the CALLER'S OWN tenant (enterprise-admin
 * only — the backend filters on tenant_id, so a foreign user id 404s).
 * Deactivating blocks sign-in entirely and ends the user's live session.
 * Instance admins have the same control instance-wide — see
 * `setInstanceUserStatus` in api/instanceAdmin.ts.
 */
export async function updateUserStatus(userId: string, isActive: boolean) {
  const res = await api.put(`/enterprise/users/${userId}/status`, {
    is_active: isActive,
  })
  return res.data as { success: boolean; is_active: boolean; sessions_ended?: number }
}

/** Clear a member's sign-in lock (too many failed attempts). Enterprise admin. */
export async function unlockUser(userId: string) {
  const res = await api.post(`/enterprise/users/${userId}/unlock`)
  return res.data as { success: boolean; was_locked: boolean }
}

/* ── Organization security policy (lockout · password · sessions) ─────── */
// Every member may read it (the change-password form states the rules); only
// the enterprise admin may save. A save names only what changed.

export interface SecurityPolicy {
  lockout: {
    free_attempts: number
    base_delay_s: number
    max_delay_s: number
    /** 0 = never hard-lock (the delays alone slow guessing). */
    lock_after: number
    reset_after_s: number
  }
  password: {
    min_length: number
    /** How many of lowercase / uppercase / digit / symbol (0-4). */
    require_classes: number
    breached_check: boolean
    /** Refuse the last N passwords (0 = off). */
    history: number
    /** Ask for a new password after N days (0 = never). */
    max_age_days: number
  }
  sessions: {
    /** Sign out after N minutes without activity (0 = no limit). */
    idle_minutes: number
    /** Sign out N hours after signing in, active or not (0 = no limit). */
    absolute_hours: number
  }
  mfa: {
    /** off: nobody is asked to set it up · optional · required. */
    mode: 'off' | 'optional' | 'required'
    /** Days a member may still sign in before setting it up, once required. */
    grace_days: number
  }
}

export type SecurityPolicyPatch = {
  [K in keyof SecurityPolicy]?: Partial<SecurityPolicy[K]>
}

export async function getSecurityPolicy() {
  const res = await api.get('/enterprise/security-policy')
  return res.data as { success: boolean; policy: SecurityPolicy }
}

export async function saveSecurityPolicy(policy: SecurityPolicyPatch) {
  const res = await api.put('/enterprise/security-policy', { policy })
  return res.data as { success: boolean; policy: SecurityPolicy }
}

export interface MemberSession {
  id: number
  user_id: string
  user_name: string
  email: string
  login_time: string | null
  last_seen_at: string | null
  ip_address: string | null
  device_info: string | null
  is_current: boolean
}

export async function listMemberSessions() {
  const res = await api.get('/enterprise/sessions')
  return res.data as { success: boolean; sessions: MemberSession[] }
}

export async function endMemberSession(id: number) {
  const res = await api.delete(`/enterprise/sessions/${id}`)
  return res.data as { success: boolean }
}

/** A member lost their authenticator: remove it and sign them out. */
export async function resetUserMfa(userId: string) {
  const res = await api.post(`/enterprise/users/${userId}/mfa/reset`)
  return res.data as { success: boolean; removed: boolean; sessions_ended: number }
}

export async function updateUserRole(userId: string, role: string) {
  const res = await api.put(`/enterprise/users/${userId}/role`, { role })
  return res.data
}

export async function inviteUser(email: string, role: string) {
  const res = await api.post('/enterprise/users/invite', { email, role })
  return res.data
}

export async function getInvitations() {
  const res = await api.get('/enterprise/invitations')
  return res.data
}

export async function cancelInvitation(invitationId: number) {
  const res = await api.delete(`/enterprise/invitations/${invitationId}`)
  return res.data
}

export async function resendInvitation(invitationId: number) {
  const res = await api.post(`/enterprise/invitations/${invitationId}/resend`)
  return res.data
}

export async function acceptInvitation(token: string) {
  const res = await api.get(`/enterprise/accept-invitation/${token}`)
  return res.data
}

/* ── Organization (tenant) details ───────────────────────────────────── */
// Editable by the organization's OWN enterprise admin (its func-admin) and by
// any instance admin, who may also pass `tenantId` to open a different
// organization. Everyone else in the tenant can read but not save.

export interface TenantDetails {
  tenant_id: string
  tenant_name: string
  email_domain: string
  web_site: string
  gst_no: string
  /**
   * The ibase SSO claim this organization is mapped to ("" = unmapped). An SSO
   * user carrying this enterprise value is provisioned into this tenant, so a
   * value already claimed by another organization is rejected with 409.
   */
  enterprise: string
  owner_name: string
  owner_email: string
  user_count: number
}

export interface TenantDetailsResponse {
  success: boolean
  tenant: TenantDetails
  can_edit: boolean
  is_instance_admin: boolean
}

export async function getTenant(tenantId?: string): Promise<TenantDetailsResponse> {
  const res = await api.get('/enterprise/tenant', {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  })
  return res.data
}

/** Only the fields present are changed; "" clears an optional one. */
export async function updateTenant(
  patch: Partial<Pick<TenantDetails,
    'tenant_name' | 'email_domain' | 'web_site' | 'gst_no' | 'enterprise'>>,
  tenantId?: string,
): Promise<{ success: boolean; tenant: TenantDetails; changed?: string[]; unchanged?: boolean }> {
  const res = await api.put('/enterprise/tenant', patch, {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  })
  return res.data
}
